/**
 * The comparison indexes a vault can be measured against.
 *
 * Promoted out of `vault-detail/components/overview/return-vs-benchmark.tsx`
 * when a second surface needed it: the strategist's per-vault page draws the
 * same comparison as the allocator's, and a benchmark list that lives inside
 * one chart component cannot be shared without importing that component.
 *
 * Deliberately NOT merged with `BENCHMARK_OPTIONS` below, the investor's
 * list. That one offers Silver and the dollar fund and omits Bitcoin; these two
 * sets answer different questions on different screens, and folding them
 * together would silently change what each screen lets a reader select.
 * Only the benchmark service's own coverage justifies changing either.
 */
export const VAULT_BENCHMARKS = [
  { symbol: 'SPX', name: 'S&P 500' },
  { symbol: 'NDX', name: 'Nasdaq 100' },
  { symbol: 'BTCUSD', name: 'Bitcoin' },
  { symbol: 'XAUUSD', name: 'Gold' }
] as const;

/**
 * The dollar series, read honestly (api 1.163.0, BE-001W).
 *
 * The backend's old `DXY` row always served the price of the Invesco DB US
 * Dollar Index Bullish Fund, an ETF, never the ICE US Dollar Index: no
 * provider it integrates carries the index. v1.163.0 renamed the row `UUP`,
 * and v1.167.0 stopped serving `DXY` at all, so the app asks for and reads
 * `UUP` only.
 */
const DOLLAR_FUND_NAME = 'US dollar fund (UUP)';

/** Whether a served series answers for the symbol that was asked for. */
export function isSameBenchmark(requested: string, served: string): boolean {
  return requested === served;
}

/** The index's readable name, or the raw symbol when we do not carry one. */
export function vaultBenchmarkName(symbol: string): string {
  return (
    VAULT_BENCHMARKS.find((entry) => entry.symbol === symbol)?.name ?? symbol
  );
}

/**
 * Comparison indexes an investor can measure their portfolio's return
 * against, on Portfolio and on Risk & liquidity. ROWA does
 * not carry a mandate benchmark yet, so this is a user choice with an equity
 * default rather than a fictional treasury index.
 */
export const BENCHMARK_OPTIONS = [
  { symbol: 'SPX', name: 'S&P 500' },
  { symbol: 'NDX', name: 'Nasdaq 100' },
  { symbol: 'XAUUSD', name: 'Gold' },
  { symbol: 'XAGUSD', name: 'Silver' },
  { symbol: 'UUP', name: DOLLAR_FUND_NAME }
] as const;

export type BenchmarkSymbol = (typeof BENCHMARK_OPTIONS)[number]['symbol'];
export const DEFAULT_BENCHMARK: BenchmarkSymbol = 'SPX';

/**
 * What to call a served series: the name the reader picked it by, for any
 * series that answers one of the investor's options, else the served name.
 * The served name can mislead (the dollar row was served as "US Dollar
 * Index" while it priced the UUP fund) and can change under us (v1.156.0
 * renamed "NASDAQ 100" to "NASDAQ-100"); the picker's name cannot.
 */
export function benchmarkLabel(series: {
  symbol: string;
  name: string;
}): string {
  return (
    BENCHMARK_OPTIONS.find((option) =>
      isSameBenchmark(option.symbol, series.symbol)
    )?.name ?? series.name
  );
}
